import * as React from 'react';
import { Stethoscope } from 'lucide-react';

import { cn, formatarDataHora } from '@/lib/utils';
import { dataHoraAtendimento, nascimentoComIdade, VAZIO } from '@/features/marcacao/formato';
import type { MarcacaoEsus } from '@/features/marcacao/tipos';

/** Bobina de 80 mm; 4 mm de cada lado ficam fora da área de impressão útil. */
const LARGURA_MM = 80;
const ALTURA_MAXIMA_MM = 240;
/**
 * Sobra no pé do cupom. Sem ela, um arredondamento de meio milímetro empurra a
 * última linha para uma segunda página — e a impressora corta um cupom em
 * branco.
 */
const FOLGA_MM = 2;
const PX_POR_MM = 96 / 25.4;

/**
 * Comprovante de marcação reduzido, para impressora térmica de 80 mm.
 *
 * Leva só o que o paciente precisa para chegar ao atendimento: cabeçalho do
 * KAVI, identificador, agendamento, procedimento e dados do usuário. Preto
 * puro, sem fundo cinza: a térmica não imprime meio-tom, só pontilha.
 *
 * A página tem a altura do conteúdo, e não a da bobina inteira, para não
 * gastar papel em branco; o limite é 240 mm. Um conteúdo que passe disso —
 * vários procedimentos, nomes longos — é reduzido em escala até caber, em vez
 * de sair partido em dois cupons.
 */
export function CupomMarcacao({
  protocolo,
  marcacao,
  chaveConfirmacao,
  impressoPor,
}: {
  protocolo: string;
  marcacao: MarcacaoEsus;
  /** Digitada por quem imprime: o índice do eSUS não a traz. */
  chaveConfirmacao: string | null;
  impressoPor: string | null;
}) {
  const conteudo = React.useRef<HTMLDivElement>(null);
  const [alturaMm, setAlturaMm] = React.useState<number | null>(null);

  // A escala é um `transform`, que não altera `offsetHeight`: medir de novo
  // depois de reduzir não realimenta a conta.
  React.useLayoutEffect(() => {
    const elemento = conteudo.current;
    if (!elemento) return;

    const medir = () => setAlturaMm(elemento.offsetHeight / PX_POR_MM || null);
    medir();

    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  const necessaria = alturaMm === null ? ALTURA_MAXIMA_MM : alturaMm + FOLGA_MM;
  const escala = Math.min(1, ALTURA_MAXIMA_MM / necessaria);
  const alturaPagina = Math.min(Math.ceil(necessaria), ALTURA_MAXIMA_MM);

  const { agendamento: ag, paciente: pac } = marcacao;
  const modulo = marcacao.modulo?.toLowerCase() === 'exame' ? 'exame' : 'consulta';

  return (
    <>
      {/* Vem depois do CSS da aplicação, e por isso prevalece sobre o A4. */}
      <style>{`@media print { @page { size: ${LARGURA_MM}mm ${alturaPagina}mm; margin: 0; } }`}</style>

      <div
        className="mx-auto overflow-hidden bg-white shadow-sm print:shadow-none"
        style={{ width: `${LARGURA_MM}mm`, height: `${alturaPagina}mm` }}
      >
        <article
          ref={conteudo}
          aria-label="Comprovante de marcação para impressora térmica"
          className="px-[4mm] pt-[3mm] pb-[1mm] text-[8.5pt] leading-tight text-black"
          style={{
            width: `${LARGURA_MM}mm`,
            transform: escala < 1 ? `scale(${escala})` : undefined,
            transformOrigin: 'top center',
          }}
        >
          {/* `div`, e não `header`: a impressão esconde todo `header`, que na
              aplicação é a barra do topo. */}
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-[1.5mm]">
              <span className="flex size-[6mm] items-center justify-center rounded-[1.2mm] bg-black text-white">
                <Stethoscope className="size-[4mm]" aria-hidden="true" />
              </span>
              <span className="text-[15pt] font-bold tracking-tight">KAVI</span>
            </div>
            <p className="mt-[0.5mm] text-[7pt]">Solicitação de Agendamentos com Especialistas</p>
            <p className="mt-[1.5mm] text-[9pt] font-bold uppercase">Comprovante de marcação</p>
            <p className="text-[7.5pt]">Protocolo KAVI {protocolo}</p>
          </div>

          <Secao titulo="Identificador da solicitação">
            {/* Sem chave, o identificador fica sozinho e centralizado: um "---"
                na bobina seria papel gasto. */}
            {chaveConfirmacao ? (
              <Grade>
                <Item rotulo="Identificador" valor={marcacao.identificador} destaque />
                <Item rotulo="Chave de Confirmação" valor={chaveConfirmacao} destaque />
              </Grade>
            ) : (
              <p className="text-center text-[14pt] font-bold tracking-wide">
                {marcacao.identificador}
              </p>
            )}
          </Secao>

          <Secao titulo="Agendamento">
            <Item rotulo="Data e Horário do Atendimento" valor={dataHoraAtendimento(ag)} destaque />
            <Item rotulo="Estabelecimento Executante" valor={ag.estabelecimento} />
            <Grade>
              <Item rotulo="CNES" valor={ag.cnes} />
              <Item rotulo="Telefone" valor={ag.telefone} />
            </Grade>
            <Item
              rotulo="Endereço"
              valor={endereco(ag.logradouro, ag.numero, ag.complemento, ag.bairro)}
            />
            <Grade>
              <Item rotulo="Município" valor={ag.municipio} />
              <Item rotulo="CEP" valor={ag.cep} />
            </Grade>
            <Item rotulo="Profissional Executante" valor={ag.profissional} />
          </Secao>

          <Secao titulo={`Atendimento para ${modulo}`}>
            {marcacao.procedimentos.length === 0 ? (
              <p className="font-bold">{VAZIO}</p>
            ) : (
              marcacao.procedimentos.map((p, i) => (
                <div key={i}>
                  <p className="font-bold uppercase">{p.nome ?? VAZIO}</p>
                  <p className="text-[7.5pt]">
                    Cód. interno {p.codigoInterno ?? VAZIO} · Cód. unificado{' '}
                    {p.codigoUnificado ?? VAZIO}
                  </p>
                </div>
              ))
            )}
          </Secao>

          <Secao titulo="Dados do usuário">
            <Item rotulo="Nome" valor={pac.nome} />
            {pac.nomeSocial && <Item rotulo="Nome Social" valor={pac.nomeSocial} />}
            <Grade>
              <Item rotulo="CNS" valor={pac.cns} />
              <Item rotulo="CPF" valor={pac.cpf} />
            </Grade>
            {/* A data com a idade não cabe em meia bobina: a coluna dela
                acompanha o texto, e o sexo fica com o resto. */}
            <Grade className="grid-cols-[auto_1fr]">
              <Item rotulo="Data de Nascimento" valor={nascimentoComIdade(pac.dataNascimento)} />
              <Item rotulo="Sexo" valor={pac.sexo} />
            </Grade>
            <Item rotulo="Nome da Mãe" valor={pac.nomeMae} />
            {/* Raça sempre, como no eSUS; tipo sanguíneo e etnia só quando
                existem — em papel de bobina, "---" é papel gasto. */}
            <Grade>
              <Item rotulo="Raça" valor={pac.racaCor} />
              {pac.tipoSanguineo && <Item rotulo="Tipo Sanguíneo" valor={pac.tipoSanguineo} />}
              {pac.etniaIndigena && <Item rotulo="Etnia Indígena" valor={pac.etniaIndigena} />}
            </Grade>
            <Item
              rotulo="Endereço"
              valor={endereco(pac.logradouro, pac.numero, pac.complemento, pac.bairro)}
            />
            <Grade>
              <Item rotulo="Município de Residência" valor={pac.municipio} />
              <Item rotulo="CEP" valor={pac.cep} />
            </Grade>
            <Item
              rotulo="Telefone(s)"
              valor={pac.telefones.length > 0 ? pac.telefones.join(', ') : null}
            />
          </Secao>

          <p className="mt-[2mm] border-t border-dashed border-black pt-[1mm] text-center text-[6.5pt]">
            Dados do eSUS Regulação · impresso em {formatarDataHora(new Date())}
            {impressoPor ? ` por ${impressoPor}` : ''}
          </p>
        </article>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------

/** `RUA X, 740, CASA - NOVO`: logradouro, número e complemento, e o bairro. */
function endereco(
  logradouro: string | null,
  numero: string | null,
  complemento: string | null,
  bairro: string | null,
): string | null {
  const rua = [logradouro, numero, complemento].filter(Boolean).join(', ');
  return [rua, bairro].filter(Boolean).join(' - ') || null;
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-[2mm] border-t border-dashed border-black pt-[1.5mm]">
      <h2 className="mb-[1mm] text-[8.5pt] font-bold uppercase">{titulo}</h2>
      <div className="flex flex-col gap-[1mm]">{children}</div>
    </section>
  );
}

function Grade({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 gap-x-[3mm] gap-y-[1mm]', className)}>{children}</div>
  );
}

function Item({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string | null;
  destaque?: boolean;
}) {
  return (
    <div className="min-w-0 break-words">
      <p className="text-[7pt]">{rotulo}</p>
      <p className={cn('font-bold uppercase', destaque ? 'text-[12pt]' : 'text-[8.5pt]')}>
        {valor?.trim() ? valor : VAZIO}
      </p>
    </div>
  );
}
